export type ClientInfo = {
  id: string;
  userAgent: string;
  connectedAt: number;
  lastPingMs: number | null;
};

export type PushPayload = {
  title: string;
  body: string;
  url?: string;
  actions?: { action: string; title: string }[];
};

// ────────────────────── game / lobby ──────────────────────

export type Role = 'player' | 'spectator';

export type LobbyInfo = {
  id: string;
  name: string;
  hostName: string;
  playerCount: number;
  maxPlayers: number;
  createdAt: number;
};

export type LobbyPlayer = {
  /** The player's CURRENT socket id. Regenerated by Socket.IO on every
   *  reconnect, so it's the right key for transport-level addressing (kick
   *  targeting, host identity) but the WRONG key for "is this me?" — use
   *  `clientId` for that. */
  id: string;
  /** Stable per-tab identity that survives socket reconnects. Supplied by the
   *  client in the handshake auth; falls back to the socket id for clients
   *  that don't send one (e.g. the load-test bots). The server reconciles a
   *  reconnecting player by this, and clients use it to recognize their own
   *  avatar — otherwise a pre-reconnect "ghost" record renders as a second
   *  copy of yourself until the dead socket times out. */
  clientId: string;
  displayName: string;
  character: number;
  role: Role;
  isHost: boolean;
  maxHeight: number;
};

export type LobbyState = {
  id: string;
  name: string;
  hostId: string;
  maxPlayers: number;
  createdAt: number;
  paused: boolean;
  players: LobbyPlayer[];
};

/** Movement/anim state. Sent on the wire as an index into this tuple (0/1/2)
 *  to keep the hot snapshot small. */
export const PLAYER_STATES = ['idle', 'run', 'air'] as const;
export type PlayerState = (typeof PLAYER_STATES)[number];

export type PlayerSnapshot = LobbyPlayer & {
  x: number;
  y: number;
  z: number;
  yaw: number;
  /** simple flag set by client when in moving/jumping state */
  state: PlayerState;
  /** last measured round-trip ping in ms, null if not yet probed */
  ping: number | null;
};

/**
 * Compact 20Hz position tuple — the hot path the tick loop broadcasts to every
 * player in a lobby each tick. A fixed-order tuple avoids repeating JSON keys
 * ~50× per snapshot, and every real is quantized to an int (positions and
 * maxHeight ×100 = cm precision; yaw ×100 = centiradians) to shrink it further:
 *
 *   [socketId, x, y, z, yaw, stateIdx, maxHeight, ping]
 *
 * Static per-player fields (displayName, character, role, isHost, clientId) are
 * deliberately NOT sent here — at 20Hz they'd dominate the payload. The client
 * joins each tuple back to the lobby roster it already holds from `lobby:state`
 * (see GameCanvas). `ping` is ms, or -1 when not yet probed. The compiled server
 * can't import this package at runtime, so it inlines the (trivial) encode; the
 * client decodes against {@link PLAYER_STATES}. Keep the two in sync.
 */
export type PackedPlayer = [string, number, number, number, number, number, number, number];

export type GameSnapshot = {
  lobbyId: string;
  t: number;
  players: PackedPlayer[];
};

/** Client-side decoded snapshot: each {@link PackedPlayer} merged with its
 *  roster entry back into the rich {@link PlayerSnapshot} the game UI consumes. */
export type DecodedSnapshot = {
  lobbyId: string;
  t: number;
  players: PlayerSnapshot[];
};

export type LocalInput = {
  x: number;
  y: number;
  z: number;
  yaw: number;
  state: PlayerState;
};

export type LobbyCreate = {
  name: string;
  displayName: string;
  character: number;
  role: Role;
};

export type LobbyJoin = {
  lobbyId: string;
  displayName: string;
  character: number;
};

export type LobbyResult =
  | { ok: true; lobby: LobbyState }
  | { ok: false; error: string };

export type AdminAction =
  | { type: 'kick'; targetId: string }
  | { type: 'pause'; paused: boolean }
  | { type: 'config'; maxPlayers?: number; name?: string }
  /** Move a player between player and spectator. Host- or admin-gated like the
   *  other actions; `targetId` may be the caller's own socket id (so an admin
   *  can drop themselves to free-cam without leaving the lobby). */
  | { type: 'role'; targetId: string; role: Role };

export type AdminResult = { ok: true } | { ok: false; error: string };

/** Global, fleet-wide configuration for the always-on dedicated ("Official")
 *  servers. Persisted in the `server_config` DB table (singleton row) and
 *  editable only by a token-elevated admin via the server-browser admin menu.
 *  Distinct from per-lobby {@link AdminAction} config, which mutates a single
 *  live lobby the admin is connected to. */
export type ServerConfig = {
  /** Master switch. When false, no dedicated servers run (Quick Play offline). */
  dedicatedEnabled: boolean;
  /** How many dedicated servers to keep materialized (default 1). */
  dedicatedCount: number;
  /** Player cap applied to every dedicated server (default 25). */
  dedicatedPlayerCap: number;
};

/** Defaults used by the client before the server reports the live config, and
 *  mirrored server-side in `apps/server/src/db/serverConfig.ts`. */
export const DEFAULT_SERVER_CONFIG: ServerConfig = {
  dedicatedEnabled: true,
  dedicatedCount: 1,
  dedicatedPlayerCap: 25,
};

export type ServerConfigResult =
  | { ok: true; config: ServerConfig }
  | { ok: false; error: string };

export type ServerDebugStats = {
  uptimeMs: number;
  totalLobbies: number;
  totalPlayers: number;
  tickHz: number;
  rxEvents: number;
  txEvents: number;
  rxBytesEst: number;
  txBytesEst: number;
  /** Total bytes broadcast in the most recent tick across all lobbies. The
   *  first signal a 50-player load test stresses — watch this climb. */
  lastTickBytes: number;
  /** node:perf_hooks event-loop delay percentiles in ms. Climbs above ~5ms
   *  means the loop is getting starved by tick fanout. */
  loopLagP50Ms: number;
  loopLagP99Ms: number;
  rssMb: number;
};

/** The base id of the dedicated "Official Server" family. The first dedicated
 *  server keeps this exact id (back-compat with the load-test bots and old
 *  clients); additional servers are suffixed `-2`, `-3`, … See
 *  {@link isDedicatedLobbyId}. Always-on, never dissolves, no human host
 *  (admin actions require token elevation). How many run is controlled by the
 *  admin-editable {@link ServerConfig}. Clients join via `lobby:quick-join`,
 *  which picks the least-full dedicated server. */
export const OFFICIAL_LOBBY_ID = 'tower-official';
export const OFFICIAL_HOST_ID = 'SYSTEM';

/** True for any dedicated/official-server lobby id (`tower-official` or
 *  `tower-official-N`). Used to pin them in the browser, persist their high
 *  scores, and gate admin behaviour. Mirrored server-side in
 *  `apps/server/src/game/lobby.ts` (which can't import this at runtime — the
 *  package is consumed as raw TS source; see the README). */
export function isDedicatedLobbyId(id: string): boolean {
  return id === OFFICIAL_LOBBY_ID || id.startsWith(`${OFFICIAL_LOBBY_ID}-`);
}

/** Obfuscation-resistant profanity check for public-facing names. Re-exported
 *  here so the client can warn live as the user types; the server has its own
 *  byte-identical copy (it can't import this at runtime — raw-TS package). The
 *  server's nameError() is the authoritative gate. See profanity.ts. */
export { containsProfanity } from './profanity.js';

export type TowerHighScore = {
  displayName: string;
  character: number;
  maxHeight: number;
  achievedAt: number; // unix ms
};

/** Server tells the client what privileges (if any) the handshake earned.
 *  Emitted once on connect; client uses it to gate admin UI affordances.
 *  The server is the source of truth — a client claiming `isAdmin` without
 *  a valid ADMIN_TOKEN gets `isAdmin: false` here. */
export type AuthStatus = {
  isAdmin: boolean;
  isBot: boolean;
};

export interface ServerToClientEvents {
  'clients:update': (clients: ClientInfo[]) => void;
  'pong:reply': (sentAt: number) => void;
  'lobby:list': (lobbies: LobbyInfo[]) => void;
  'lobby:state': (state: LobbyState | null) => void;
  'game:snapshot': (snap: GameSnapshot) => void;
  'debug:server-stats': (stats: ServerDebugStats) => void;
  /** Pushed to lobby-browser room whenever the official-server leaderboard
   *  changes (player leaves, new high score posted). */
  'tower:leaderboard': (top: TowerHighScore[]) => void;
  /** Emitted once per connection after the handshake middleware runs. */
  'auth:status': (status: AuthStatus) => void;
}

export interface ClientToServerEvents {
  'status:join': () => void;
  'status:leave': () => void;
  'ping:probe': (sentAt: number) => void;
  'lobby:browser:join': () => void;
  'lobby:browser:leave': () => void;
  'lobby:create': (opts: LobbyCreate, cb: (r: LobbyResult) => void) => void;
  'lobby:join': (opts: LobbyJoin, cb: (r: LobbyResult) => void) => void;
  /** Drop into the always-on Official Server lobby in one click. Idempotent —
   *  if the caller is already in the official lobby, returns its current state. */
  'lobby:quick-join': (opts: Pick<LobbyJoin, 'displayName' | 'character'>, cb: (r: LobbyResult) => void) => void;
  'lobby:leave': () => void;
  'game:input': (input: LocalInput) => void;
  'admin:action': (action: AdminAction, cb: (r: AdminResult) => void) => void;
  /** Runtime privilege elevation — present a token, get isAdmin on success.
   *  Beats handshake auth because failure can be surfaced in the UI without
   *  a page reload. Server flips socket.data.isAdmin and re-emits auth:status. */
  'admin:elevate': (token: string, cb: (r: AdminResult) => void) => void;
  /** Drop admin privileges on this socket. No-op if not currently elevated. */
  'admin:logout': (cb: (r: AdminResult) => void) => void;
  /** Read the global dedicated-server config (admin only). Used by the
   *  server-browser admin menu to populate its form. */
  'admin:get-config': (cb: (r: ServerConfigResult) => void) => void;
  /** Update the global dedicated-server config (admin only). The server
   *  persists it, reconciles the live dedicated lobbies (spawning/removing
   *  servers, re-capping players), and acks the clamped result. */
  'admin:set-config': (patch: Partial<ServerConfig>, cb: (r: ServerConfigResult) => void) => void;
  /** Wipe every persisted Tower high score (admin only). Irreversible. The
   *  server truncates `tower_high_scores` and rebroadcasts the now-empty
   *  leaderboard to all lobby-browser subscribers. */
  'admin:clear-leaderboard': (cb: (r: AdminResult) => void) => void;
  'debug:subscribe': () => void;
  'debug:unsubscribe': () => void;
}

export type AnySocketEvent =
  | keyof ServerToClientEvents
  | keyof ClientToServerEvents;

// ────────────────────── PWA environment branding ──────────────────────

/** Which deployment a given hostname represents. ONE Cloud Run container
 *  serves all of them (yesweb.app, the pwa.dipduo.com fallback, localhost),
 *  so the PWA identity — manifest name/short_name/icons/theme — is
 *  differentiated at request time by Host. Without this, Chrome's deep-link
 *  "open with" picker shows three identically-named "YesWeb" entries because
 *  the manifest `id` is already origin-scoped (three distinct installed apps)
 *  but everything else is identical. The server applies this when serving
 *  `/manifest.webmanifest`; the web app uses it for the in-app env badge and
 *  theme-color tint. */
export type PwaEnvKey = 'prod' | 'test' | 'dev';

export type PwaEnv = {
  key: PwaEnvKey;
  /** Manifest `name` + installed-app title + deep-link picker label. */
  name: string;
  /** Manifest `short_name` (home-screen / launcher label). */
  shortName: string;
  /** Manifest theme_color + background_color (standalone title bar + splash)
   *  and the browser UI tint via the <meta name="theme-color"> tag. */
  themeColor: string;
  /** Hue rotation (degrees) applied to the base sky-blue icon set to tint it
   *  per env. 0 = the original brand icon. Used both by the build-time icon
   *  generator (scripts/gen-env-icons.mjs) and the in-app logo CSS filter. */
  iconHue: number;
  /** Filename suffix for this env's tinted icon variants (e.g. '-test' ⇒
   *  pwa-512x512-test.png). Empty for prod, which uses the base icons. */
  iconSuffix: string;
  /** Accent color for the in-app env badge pill. */
  accent: string;
};

export const PWA_ENVS: Record<PwaEnvKey, PwaEnv> = {
  prod: { key: 'prod', name: 'YesWeb',      shortName: 'YesWeb',      themeColor: '#0f172a', iconHue: 0,   iconSuffix: '',      accent: '#38bdf8' },
  test: { key: 'test', name: 'YesWeb Test', shortName: 'YesWeb Test', themeColor: '#2e1065', iconHue: 95,  iconSuffix: '-test', accent: '#a78bfa' },
  dev:  { key: 'dev',  name: 'YesWeb Dev',  shortName: 'YesWeb Dev',  themeColor: '#451a03', iconHue: 200, iconSuffix: '-dev',  accent: '#fbbf24' },
};

/** Map a hostname (`req.hostname` server-side, `location.hostname` client-side)
 *  to its PWA env:
 *    prod → yesweb.app, www.yesweb.app
 *    test → pwa.dipduo.com  (the legacy/fallback domain)
 *    dev  → localhost, 127.0.0.1, *.run.app, LAN IPs, anything else
 *  NOTE: the COMPILED server can't import this at runtime (the package is
 *  consumed as raw TS source — Node would choke on the .ts extension; see the
 *  README + isDedicatedLobbyId above), so apps/server/src/pwaManifest.ts keeps
 *  a hand-mirror of this mapping. Keep the two in sync. */
export function resolvePwaEnv(hostname: string): PwaEnv {
  const h = (hostname || '').toLowerCase().split(':')[0];
  if (h === 'yesweb.app' || h === 'www.yesweb.app') return PWA_ENVS.prod;
  if (h === 'pwa.dipduo.com') return PWA_ENVS.test;
  return PWA_ENVS.dev;
}
